import {
    decryptKeyTransferEnvelope,
    decryptWithX25519,
    type ECDHKeyPair,
    type EncryptedPrivateKey,
    encodeBase64Url,
    generateECDHKeyPair,
    type KeyTransferEnvelope,
    type WrappedKey,
} from '@sisumail/crypto';

// Re-export common types
export type { ECDHKeyPair, EncryptedPrivateKey, WrappedKey };
export { encodeBase64Url };

/**
 * Generate ephemeral pairing keys for the extension/app
 */
export async function generatePairingKeys(): Promise<ECDHKeyPair> {
    return generateECDHKeyPair();
}

/**
 * Unwrap the Mailbox Key using the shared secret and encrypted bundle
 *
 * @param encryptedKey - The encrypted key bundle from the API (finalized)
 * @param privateKey - The device's ephemeral private key
 * @param identityId - The username/identityId
 * @param requestId - The pairing request ID
 */
export async function recoverMailboxKey(
    encryptedKey: KeyTransferEnvelope,
    privateKey: CryptoKey,
    identityId: string,
    requestId: string
): Promise<Uint8Array> {
    // Determine format.
    // If it's the V1 Key Envelope:
    if (encryptedKey.kt_v === 1) {
        return decryptKeyTransferEnvelope(encryptedKey, privateKey, identityId, requestId);
    }

    throw new Error('Unsupported key format');
}

/**
 * Decrypt a message preview using the Mailbox Key (X25519 Private Key)
 */
export async function decryptMessagePreview(
    metaCt: string,
    mailboxKey: Uint8Array
): Promise<{ subject: string; preview: string }> {
    try {
        // Convert Uint8Array to Base64URL string expected by library
        const privateKeyB64 = encodeBase64Url(mailboxKey);

        const decryptedBytes = await decryptWithX25519(metaCt, privateKeyB64);
        const json = new TextDecoder().decode(decryptedBytes);

        const payload = JSON.parse(json);
        return {
            subject: payload.subject || '(No Subject)',
            preview: payload.preview || '',
        };
    } catch (_e) {
        return {
            subject: 'Decryption Failed',
            preview: 'Could not decrypt message content.',
        };
    }
}
